import { Box, Flex, Heading, Skeleton, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetFinancialCovenantCalculationsQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  chartsInitialEndDateMillis,
  covenantsInitialStartDateMillis,
  ExpandedFinancialCovenantsCalculation,
  ExpandedPortfolioCovenantChartData,
  getAllUniqueCovenantKeys,
  PortfolioChartConfig,
  PortfolioChartFormat,
} from '../shared/portfolioChartUtils';
import { PortfolioVisibilityGroupButton } from '../shared/PortfolioVisibilityGroupButton';
import { FinancialCovenantChartTemplate } from './FinancialCovenantChartTemplate';
import { PortfolioCovenantChartTemplate } from './PortfolioCovenantChartTemplate';
import { useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery } from 'packages/borrowing-base/requests/portfolioCovenants.gql.generated';
import { colors, transparency } from 'packages/common-frontend/colors';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { useGetCurrentCaAndWip } from 'packages/creditAgreements/ui/creditAgreementWip/utils/useGetCurrentCaAndWip';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const covenantsDisplayModes = ['all', 'portfolio', 'financial'] as const;
type CovenantsChartDisplayMode = typeof covenantsDisplayModes[number];
const isCovenantsChartDisplayMode = (
  test: any,
): test is CovenantsChartDisplayMode => {
  return (
    test && typeof test === 'string' && _.includes(covenantsDisplayModes, test)
  );
};

const covenantsDisplayModeQueryParam = 'display';

// Each config should be in the order that the chart components are layered
// ie. the last item is the top-most layer on render
export const createPortfolioCovenantsChartConfig = (
  format: PortfolioChartFormat,
): PortfolioChartConfig<keyof ExpandedPortfolioCovenantChartData> => {
  return {
    result: {
      chartType: 'none',
      field: 'result',
      label: 'Result',
      format: 'boolean',
      color: 'black',
    },
    value: {
      chartType: 'line',
      field: 'value',
      label: 'Value',
      format,
      color: colors.chart.yellow,
    },
  };
};

// Each config should be in the order that the chart components are layered
// ie. the last item is the top-most layer on render
export const createFinancialCovenantsChartConfig = (
  format: PortfolioChartFormat,
): PortfolioChartConfig<keyof ExpandedFinancialCovenantsCalculation> => {
  return {
    isPassed: {
      chartType: 'none',
      field: 'isPassed',
      label: 'Status',
      format: 'boolean',
      color: 'black',
    },
    value: {
      chartType: 'bar',
      field: 'value',
      label: 'Actual',
      format,
      color: `${colors.chart.teal}${transparency[50]}`,
      activeColor: colors.chart.teal,
      conditionalStyle: {
        condition: (datapoint: ExpandedFinancialCovenantsCalculation) => {
          return datapoint.isPassed === false;
        },
        style: { fill: colors.brick[500] },
      },
    },
    limitValue: {
      chartType: 'dashedLine',
      field: 'limitValue',
      label: 'Limit',
      format,
      color: colors.black[95],
    },
  };
};

type CovenantsContainerProps = {
  creditFacilityId: string;
};
export const CovenantsContainer = ({
  creditFacilityId,
}: CovenantsContainerProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQueryParams();
  const queryDisplayMode = query.get(covenantsDisplayModeQueryParam);
  const displayMode = isCovenantsChartDisplayMode(queryDisplayMode)
    ? queryDisplayMode
    : 'all';

  const {
    data: covenantsData,
    loading: isCovenantsLoading,
  } = useGetFinancialCovenantCalculationsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      startDate: covenantsInitialStartDateMillis,
      endDate: chartsInitialEndDateMillis,
    },
  });

  const {
    loading: isPortfolioCovenantsLoading,
    data: allPortfolioCovenantsData,
  } = useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      mode: 'RESULT',
    },
  });

  const { currentCreditAgreementFullArtifact } = useGetCurrentCaAndWip({
    creditFacilityId,
    creditAgreementsPath: '/',
  });

  if (
    isCovenantsLoading ||
    !covenantsData ||
    isPortfolioCovenantsLoading ||
    !allPortfolioCovenantsData ||
    !currentCreditAgreementFullArtifact
  ) {
    return (
      <Skeleton height="600px" data-testid="credit-agreements-route-loading" />
    );
  }

  // once portcovs are added, update condition to no fincov && no portcov to display alert
  if (
    !covenantsData.financialCovenantCalculations &&
    !allPortfolioCovenantsData.getSuccessPortfolioCovenantsByCreditFacilityId
      .length
  ) {
    return (
      <InfoAlertWithDescription description="No covenant calculations are available in the selected time period." />
    );
  }

  const { portfolioCovenants } = currentCreditAgreementFullArtifact;
  const financialCovenantsConfig =
    covenantsData.financialCovenantCalculations?.config ?? [];

  const uniquePortfolioCovenantKeys = getAllUniqueCovenantKeys(
    allPortfolioCovenantsData.getSuccessPortfolioCovenantsByCreditFacilityId,
  );

  const onSetDisplayMode = (selectedDisplayMode: CovenantsChartDisplayMode) => {
    const covenantsBaseRoute = '/portfolio/covenants';
    const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}&${covenantsDisplayModeQueryParam}=${selectedDisplayMode}`;

    navigate(`${covenantsBaseRoute}${queryParams}`);
  };

  return (
    <Box>
      <Flex mb="32px" style={{ gap: '16px' }}>
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'all'}
          label={`Show All ${
            flags?.displayPortfolioCovenants
              ? financialCovenantsConfig.length +
                uniquePortfolioCovenantKeys.length
              : financialCovenantsConfig.length
          }`}
          onClick={() => onSetDisplayMode('all')}
        />
        {flags?.displayPortfolioCovenants && (
          <PortfolioVisibilityGroupButton
            selected={displayMode === 'portfolio'}
            label={`Portfolio Covenants ${uniquePortfolioCovenantKeys.length}`}
            onClick={() => onSetDisplayMode('portfolio')}
          />
        )}
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'financial'}
          label={`Financial Covenants ${financialCovenantsConfig.length}`}
          onClick={() => onSetDisplayMode('financial')}
        />
      </Flex>
      {(displayMode === 'all' || displayMode === 'portfolio') &&
        flags?.displayPortfolioCovenants && (
          <>
            <Heading as="h3" fontSize="24px" fontWeight={600} mb="24px">
              Portfolio Covenants
            </Heading>
            {_.map(uniquePortfolioCovenantKeys, (portfolioCovenantKey) => {
              // it's possible for the covenantKey of a portfolio covenant calculation to no longer exist in the current CA, if it's been deleted
              // we will still show the calculations and graphs as historical data, but will not be able to parse out the thresholdType (min vs max) later downstream, which is fine
              const creditAgreementPortfolioCovenant = _.find(
                portfolioCovenants,
                (portfolioCovenant) =>
                  portfolioCovenant.covenantKey === portfolioCovenantKey,
              );

              return (
                <PortfolioCovenantChartTemplate
                  creditFacilityId={creditFacilityId}
                  chartTitle={commonUtils.toTitleCase(portfolioCovenantKey)}
                  covenantKey={portfolioCovenantKey}
                  chartConfig={createPortfolioCovenantsChartConfig(
                    'percentage',
                  )}
                  key={`portfolio-covenant-chart-${portfolioCovenantKey}`}
                  creditAgreementPortfolioCovenant={
                    creditAgreementPortfolioCovenant
                  }
                />
              );
            })}
          </>
        )}
      {(displayMode === 'all' || displayMode === 'financial') && (
        <>
          <Heading as="h3" fontSize="24px" fontWeight={600} mb="24px">
            Financial Covenants
          </Heading>
          {_.map(financialCovenantsConfig, (config) => {
            const { key, valueType } = config;
            return (
              <FinancialCovenantChartTemplate
                creditFacilityId={creditFacilityId}
                chartTitle={commonUtils.toTitleCase(key)}
                covenantKey={key}
                chartConfig={createFinancialCovenantsChartConfig(valueType)}
                key={`financial-covenant-chart-${key}`}
              />
            );
          })}
        </>
      )}
    </Box>
  );
};
